import { useState } from 'react';
import { GoSearch, GoDotFill } from 'react-icons/go';
import { ExtendedDbCloudWalletUserInfo } from '@shared/types/db/user';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { AggregatorResponse } from '@shared/types/services/aggregator-response';
import ImageWithFallback from '@/components/shared/ImageWithFallback';
import Loading from '@/components/shared/Loading';
import useGetUserProfileInfo from '@/hooks/auth/useGetUserProfileInfo';
import { TFeedMode } from '@/pages/feed';
import { useGetUsernames } from '@/hooks/auth/useGetUsernames';
import { useFollowersQuery, useFollowingQuery } from '@/hooks/following/useFollowing';
import PeopleItem from './PeopleItem';
import { ScrollArea } from '@/components/ui/scroll-area';
import BuySellPeople from './BuySellPeople';
import ReferralBanner from '@/components/portfolio/desktop/ReferralBanner';
const modes = ['Following', 'Followers', 'Requests', 'Requested'];
const modeNames = ['Friends', 'Followers', 'Requests', 'Requested'];
interface Props {
  setShowReferModal?: (show: boolean) => void;
}
export default function PeopleContent(p: Props) {
  const [mode, setMode] = useState<TFeedMode>('Feed');
  const [prevState, setPrevState] = useState<TFeedMode>('Following');
  const {
    userName: username
  } = useGetUserProfileInfo();
  const [searchQuery, setSearchQuery] = useState('');
  const {
    data: getUsernamesData,
    isLoading: isLoadingGetUsernamesData
  } = useGetUsernames({
    phrase: searchQuery.startsWith('@') ? searchQuery : '@' + searchQuery
  });
  const {
    data: allFollowersData,
    isLoading: allFollowersLoading
  } = useFollowersQuery({
    username: username
  });
  const {
    data: allFollowingData,
    isLoading: allFollowingLoading
  } = useFollowingQuery({
    username: username
  });
  const allFollowers = allFollowersData?.filter((x, idx) => {
    //remove duplicates
    return allFollowersData.findIndex(y => y.username === x.username) === idx;
  });
  const allFollowing = {
    feed: allFollowingData?.feed,
    following: allFollowingData?.following?.filter((x, idx) => {
      //remove duplicates
      return allFollowingData?.following?.findIndex(y => y.username === x.username) === idx;
    })
  };
  const incomingRequests = allFollowers?.filter(x => x.status === 'pending');
  const followers = allFollowers?.filter(x => x.status === 'public' || x.status === 'private');
  const outgoingRequests = allFollowing?.following?.filter(x => x.status === 'pending');
  const following = allFollowing?.following?.filter(x => x.status === 'public' || x.status === 'private' || x.status !== 'pending');
  const requested = allFollowing?.following?.filter(x => x.status === 'pending');
  const feed = allFollowing?.feed;
  const searchResults = (getUsernamesData as ExtendedDbCloudWalletUserInfo[])?.length ? (getUsernamesData as ExtendedDbCloudWalletUserInfo[])?.map(x => {
    return {
      userIsFollowing: following?.some(y => y.username === x.username),
      userIsPending: outgoingRequests?.some(y => y.username === x.username),
      ...x
    };
  }).filter(x => x.username !== username).filter((x, idx) => {
    return (getUsernamesData as ExtendedDbCloudWalletUserInfo[])?.findIndex(y => y.username === x.username) === idx;
  }) : getUsernamesData;
  const profilesToShow = mode == 'Following' ? following : mode == 'Followers' ? followers : mode == 'Requests' ? incomingRequests : mode == 'Feed' ? feed?.filter(order => {
    try {
      const quotingEngineOrder = order?.result?.decodedResponse?.quote;
      const {
        fees
      } = quotingEngineOrder as AggregatorResponse;
      const {
        grossUsd
      } = fees;
      return grossUsd > 10;
    } catch (_) {
      return false;
    }
  }) : mode == 'Requested' ? requested : null;
  return <div className='flex-1 flex flex-col' data-sentry-component="PeopleContent" data-sentry-source-file="PeopleContent.tsx">
      <div className='flex space-x-4 mb-1 px-3'>
        <p onClick={() => setMode('Feed')} className={`cursor-pointer text-[20px] ${mode === 'Feed' ? 'text-genius-cream' : 'text-genius-cream/50'} hover:text-genius-cream/75 transition-colors`}>
          Feed
        </p>
        <p onClick={() => setMode(prevState)} className={`flex items-center cursor-pointer text-[20px] ${mode !== 'Feed' ? 'text-genius-cream' : 'text-genius-cream/50'} hover:text-genius-cream/75 transition-colors`}>
          People
          {incomingRequests && incomingRequests?.length > 0 && <GoDotFill className='text-genius-pink' />}
        </p>
      </div>

      <div className='w-full px-3 flex items-center text-genius-cream justify-between relative py-2  gap-2'>
        <div className='w-full relative'>
          <GoSearch className='w-[14px] absolute left-2 top-3 text-genius-cream/50' data-sentry-element="GoSearch" data-sentry-source-file="PeopleContent.tsx" />
          <input type='text' value={searchQuery} onChange={e => {
          if (e.target.value == '' || !e.target.value) {
            setSearchQuery('');
            setMode(prevState);
          } else {
            setMode(null);
            setSearchQuery(e.target.value);
          }
        }} placeholder='Find Genius Users' className='h-[38px] rounded-md text-sm bg-genius-blue  w-full pl-8 px-3 text-genius-cream' />
        </div>
      </div>
      <div className='flex space-x-3 px-3 overflow-x-auto no-scrollbar'>
        {mode !== 'Feed' && !searchQuery && modes.map((m, index) => m !== 'feed' && <div key={m} className={`flex items-center text-[16px] relative cursor-pointer px-1 py-1 mt-1 rounded-full ${mode === m ? 'text-genius-cream' : 'text-genius-cream/50'}`} onClick={() => {
        setSearchQuery('');
        setMode(m as any);
        setPrevState(m as any);
      }}>
                  {modeNames[index]}
                  {m === 'Requests' && incomingRequests && incomingRequests?.length > 0 && <GoDotFill className='text-genius-pink' />}
                </div>)}
      </div>

      {searchQuery ? <>
          {isLoadingGetUsernamesData ? <div className='mt-24'>
              <Loading />
            </div> : <>
              {(searchResults as {
          error?: string;
        })?.error ? <div className='w-full flex flex-col items-center justify-center mt-32'>
                  <ImageWithFallback src='/static/icons/nothingcircle.png' alt='logo' height={100} width={100} className='rounded-full' />

                  <div className='px-4'>
                    <p className={`mt-[50px] text-center text-genius-cream/50 text-[24px] font-semibold leading-8`}>
                      No users found
                    </p>
                    <p className='text-[12px] text-genius-cream/50 mt-10 w-[240px] text-center'>
                      Try searching for another username.
                    </p>
                  </div>
                </div> : <ScrollArea className='h-full border-genius-blue border-t-[1px]'>
                  <div className='p-2 pt-0 bg-genius-indigo'>
                    {(searchResults as ExtendedDbCloudWalletUserInfo[])?.map((x: ExtendedDbCloudWalletUserInfo, idx) => {
              return <PeopleItem mode={mode} key={idx} user={x} ownUsername={username} />;
            })}
                  </div>
                </ScrollArea>}
            </>}
        </> : <>
          {allFollowingLoading || allFollowersLoading ? <div className='mt-24'>
              <Loading />
            </div> : <>
              {profilesToShow?.length < 1 ? <div className='h-full'>
                  <ReferralBanner setShowReferModal={p.setShowReferModal} />
                </div> : <div className='flex-1 px-2'>
                  <AutoSizer>
                    {({
              height,
              width
            }) => <List key={`${mode}-${searchQuery}`} height={height} itemCount={profilesToShow.length} itemSize={70} width={width} className='no-scrollbar'>
                        {({
                index,
                style
              }) => <div style={style}>
                            {mode === 'Feed' ? <BuySellPeople order={profilesToShow[index]} /> : <PeopleItem user={profilesToShow[index]} mode={mode} ownUsername={username} />}
                          </div>}
                      </List>}
                  </AutoSizer>
                </div>}
            </>}
        </>}
    </div>;
}